import { action, observable, runInAction } from 'mobx';

import { RampPgApiSdk } from '~api/RampPgApiSdk';
import { StaticLookupUrls } from '~domain/entities/Config';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';

type OptionPath = number[];

// Might contain additional properties, like postal code for a district option
export interface ICascadingDropdownOption {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    [key: string]: any; // additional form values
    value: string;
    label: string;
    path: OptionPath;
}

interface IStaticLookupDataOption {
    value: string;
    label?: string;
    subtree?: IStaticLookupDataOption[];
}

export class DropdownDataStore {
    private api: RampPgApiSdk;
    private staticLookupUrls?: StaticLookupUrls;

    constructor() {
        this.api = diContainer.get(serviceNames.rampPgApiSdk);
    }

    /**
     * @description Store all static lookup data fetched from staticLookupUrls.
     */
    @observable public staticLookupDataCollection: Map<
        string,
        IStaticLookupDataOption[]
    > = new Map();

    /**
     * @description Cache field identifier and path mapping
     */
    @observable public nodePaths: Map<string, OptionPath> = new Map();

    public setup = (staticLookupUrls?: StaticLookupUrls) => {
        this.staticLookupUrls = staticLookupUrls;
    };

    public loadStaticLookupData = async (staticLookupName: string) => {
        const url = this.staticLookupUrls?.[staticLookupName];
        if (url && !this.staticLookupDataCollection.has(staticLookupName)) {
            try {
                const dropdownData = await this.api.loadStaticJsonContent(url);
                runInAction(() => {
                    this.staticLookupDataCollection.set(staticLookupName, dropdownData);
                });
            } catch (e) {
                console.error(e);
            }
        }
    };

    public getParentPath = (staticLookupName: string, dependsOnField?: string) => {
        return dependsOnField
            ? this.nodePaths.get(this.getFieldIdentifier(staticLookupName, dependsOnField)) ?? []
            : [];
    };

    /**
     * For Dropdown field to get options data by staticLookupName and dependsOnField
     */
    public getStaticOptions = (
        staticLookupName: string,
        dependsOnField?: string
    ): ICascadingDropdownOption[] => {
        const parentPath = this.getParentPath(staticLookupName, dependsOnField);
        // depends on field value is not set
        if (dependsOnField && !parentPath.length) {
            return [];
        }
        const lookupData = this.staticLookupDataCollection.get(staticLookupName) ?? [];

        return this.getSubtree(lookupData, parentPath).map((option, index) => {
            const { value, label, subtree, ...additionalValues } = option;

            return {
                ...additionalValues,
                value,
                label: label || value,
                path: parentPath.concat(index)
            };
        });
    };

    @action
    public setCurrentPath = (staticLookupName: string, fieldName: string, path: number[]) => {
        this.nodePaths.set(this.getFieldIdentifier(staticLookupName, fieldName), path);
    };

    private getFieldIdentifier = (staticLookupName: string, fieldName: string) =>
        `${staticLookupName}/${fieldName}`;

    private getSubtree = (
        options: IStaticLookupDataOption[],
        parentPath: number[]
    ): IStaticLookupDataOption[] => {
        const index = parentPath.shift();

        return index !== undefined
            ? this.getSubtree(options[index].subtree as IStaticLookupDataOption[], parentPath)
            : options;
    };
}
